'use client';

import type { MysteryRoomSurfaceTexture } from './mapTypes';

export type MysteryRoomDefaultTextureStyle = 'realism' | 'horror' | 'decor' | 'sci_fi' | 'pixel';

export type MysteryRoomDefaultTextureDefinition = {
  id: string;
  label: string;
  style: MysteryRoomDefaultTextureStyle;
  category: 'floor' | 'wall' | 'decor' | 'technical';
  subcategory: string;
  recommendedFor: Array<'floor' | 'wall' | 'object' | 'door' | 'light' | 'trigger'>;
  tileSize: number;
  assetUrl: string;
  assetName: string;
  assetMimeType: 'image/svg+xml' | 'image/webp';
};

const PROJECT_TEXTURE_BASE_URL = '/games/mystery-room/textures';
const PROJECT_TEXTURE_STYLE_LABELS: Record<Exclude<MysteryRoomDefaultTextureStyle, 'pixel'>, string> = {
  realism: 'Реализм',
  horror: 'Хоррор',
  decor: 'Декор',
  sci_fi: 'Sci-Fi',
};

const PROJECT_TEXTURE_RECOMMENDED_FOR: MysteryRoomDefaultTextureDefinition['recommendedFor'] = [
  'floor',
  'wall',
  'object',
  'door',
  'light',
  'trigger',
];

function projectTexture(
  index: number,
  style: Exclude<MysteryRoomDefaultTextureStyle, 'pixel'>,
  slug: string,
  label: string,
  category: MysteryRoomDefaultTextureDefinition['category'],
  subcategory: string,
): MysteryRoomDefaultTextureDefinition {
  const prefix = String(index).padStart(2, '0');
  const assetName = `${prefix}-${style}-${slug}.webp`;
  return {
    id: `${style}_${slug.replace(/-/g, '_')}`,
    label: `${PROJECT_TEXTURE_STYLE_LABELS[style]} / ${label}`,
    style,
    category,
    subcategory,
    recommendedFor: PROJECT_TEXTURE_RECOMMENDED_FOR,
    tileSize: 512,
    assetUrl: `${PROJECT_TEXTURE_BASE_URL}/${assetName}`,
    assetName,
    assetMimeType: 'image/webp',
  };
}

function svgTextureDataUrl(svg: string): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function tileSvg(size: number, body: string, background = '#0f172a'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="${background}"/>${body}</svg>`;
}

export const MYSTERY_ROOM_DEFAULT_TEXTURES: MysteryRoomDefaultTextureDefinition[] = [
  projectTexture(1, 'realism', 'stone-pebble-granite', 'Каменная крошка', 'floor', 'stone pebble granite'),
  projectTexture(2, 'realism', 'white-marble-veins', 'Белый мрамор', 'floor', 'white marble veins'),
  projectTexture(3, 'realism', 'grey-stone-blocks', 'Серые каменные блоки', 'wall', 'grey stone blocks'),
  projectTexture(4, 'realism', 'riveted-steel-plates', 'Стальные плиты с заклепками', 'wall', 'riveted steel plates'),
  projectTexture(5, 'realism', 'dry-brown-earth', 'Сухая земля', 'floor', 'dry brown earth'),
  projectTexture(6, 'realism', 'warm-wood-planks', 'Теплые доски', 'floor', 'warm wood planks'),
  projectTexture(7, 'realism', 'dark-matte-concrete', 'Темный матовый бетон', 'floor', 'dark matte concrete'),
  projectTexture(8, 'realism', 'beige-ceramic-tile', 'Бежевая плитка', 'floor', 'beige ceramic tile'),
  projectTexture(9, 'realism', 'dense-green-moss', 'Плотный мох', 'floor', 'dense green moss'),
  projectTexture(10, 'realism', 'red-brick-wall', 'Красный кирпич', 'wall', 'red brick wall'),
  projectTexture(11, 'realism', 'green-glass-blocks', 'Зеленые стеклоблоки', 'wall', 'green glass blocks'),
  projectTexture(12, 'realism', 'light-poured-concrete', 'Светлый бетон', 'floor', 'light poured concrete'),
  projectTexture(13, 'realism', 'steel-floor-panels', 'Стальной панельный пол', 'technical', 'steel floor panels'),
  projectTexture(14, 'realism', 'varnished-wood-floor', 'Лакированный деревянный пол', 'floor', 'varnished wood floor'),
  projectTexture(15, 'realism', 'rough-black-asphalt', 'Шероховатый асфальт', 'floor', 'rough black asphalt'),
  projectTexture(16, 'realism', 'soft-white-marble', 'Мягкий белый мрамор', 'floor', 'soft white marble'),
  projectTexture(17, 'realism', 'rocky-clay-ground', 'Каменистая глина', 'floor', 'rocky clay ground'),
  projectTexture(18, 'realism', 'round-pebble-floor', 'Круглая галька', 'floor', 'round pebble floor'),
  projectTexture(19, 'horror', 'rusted-metal-plates', 'Ржавые металлические плиты', 'wall', 'rusted metal plates'),
  projectTexture(20, 'horror', 'aged-dark-wood', 'Старое темное дерево', 'floor', 'aged dark wood'),
  projectTexture(21, 'horror', 'black-ridged-stone', 'Черный ребристый камень', 'floor', 'black ridged stone'),
  projectTexture(22, 'horror', 'cracked-black-floor', 'Черный потрескавшийся пол', 'floor', 'cracked black floor'),
  projectTexture(23, 'horror', 'charcoal-gravel', 'Угольная крошка', 'floor', 'charcoal gravel'),
  projectTexture(24, 'horror', 'black-marble-veins', 'Черный мрамор', 'floor', 'black marble veins'),
  projectTexture(25, 'horror', 'dark-stone-bricks', 'Темная каменная кладка', 'wall', 'dark stone bricks'),
  projectTexture(26, 'horror', 'grimy-glass-tile', 'Грязная стеклянная плитка', 'floor', 'grimy glass tile'),
  projectTexture(27, 'horror', 'aged-round-mosaic', 'Старая круглая мозаика', 'floor', 'aged round mosaic'),
  projectTexture(28, 'horror', 'burnt-fiber-mat', 'Обожженное волокно', 'floor', 'burnt fiber mat'),
  projectTexture(29, 'horror', 'old-dark-brick', 'Старый темный кирпич', 'wall', 'old dark brick'),
  projectTexture(30, 'horror', 'worn-cracked-concrete', 'Потертый бетон с трещинами', 'floor', 'worn cracked concrete'),
  projectTexture(31, 'decor', 'mossy-cobblestone', 'Мшистая брусчатка', 'floor', 'mossy cobblestone'),
  projectTexture(32, 'decor', 'mixed-gravel-path', 'Гравийная дорожка', 'floor', 'mixed gravel path'),
  projectTexture(33, 'decor', 'mossy-forest-ground', 'Лесная земля со мхом', 'floor', 'mossy forest ground'),
  projectTexture(34, 'decor', 'weathered-park-planks', 'Выветренные доски', 'floor', 'weathered park planks'),
  projectTexture(35, 'decor', 'white-gold-marble-tile', 'Белая плитка с золотом', 'floor', 'white gold marble tile'),
  projectTexture(36, 'decor', 'embossed-dark-tile', 'Темная рельефная плитка', 'floor', 'embossed dark tile'),
  projectTexture(37, 'decor', 'gray-brick-pavement', 'Серая кирпичная кладка', 'floor', 'gray brick pavement'),
  projectTexture(38, 'decor', 'blue-stained-mosaic', 'Синяя мозаика', 'floor', 'blue stained mosaic'),
  projectTexture(39, 'decor', 'ornate-ceramic-pattern', 'Орнаментальная керамика', 'floor', 'ornate ceramic pattern'),
  projectTexture(40, 'decor', 'pale-stone-pavers', 'Светлые каменные плиты', 'floor', 'pale stone pavers'),
  projectTexture(41, 'decor', 'grass-with-flowers', 'Трава с цветами', 'floor', 'grass with flowers'),
  projectTexture(42, 'decor', 'mossy-stone-wall', 'Каменная стена со мхом', 'wall', 'mossy stone wall'),
  projectTexture(43, 'sci_fi', 'road-lane-markings', 'Дорожная разметка', 'technical', 'road lane markings'),
  projectTexture(44, 'sci_fi', 'black-gold-tech-grid', 'Черная техносетка с золотом', 'technical', 'black gold tech grid'),
  projectTexture(45, 'sci_fi', 'industrial-sci-fi-panel', 'Индустриальная sci-fi панель', 'technical', 'industrial sci fi panel'),
  projectTexture(46, 'sci_fi', 'dark-wood-metal-seams', 'Темное дерево с металлическими швами', 'floor', 'dark wood metal seams'),
  projectTexture(47, 'sci_fi', 'purple-neon-brick', 'Кирпич с фиолетовым неоном', 'wall', 'purple neon brick'),
  projectTexture(48, 'sci_fi', 'purple-dark-rock', 'Темная порода с фиолетовым свечением', 'floor', 'purple dark rock'),
  projectTexture(49, 'sci_fi', 'cyan-lit-gravel', 'Гравий с голубой подсветкой', 'floor', 'cyan lit gravel'),
  projectTexture(50, 'sci_fi', 'blue-holographic-grid', 'Синяя голографическая сетка', 'technical', 'blue holographic grid'),
  projectTexture(51, 'sci_fi', 'green-bioluminescent-ground', 'Зеленая биолюминесцентная поверхность', 'floor', 'green bioluminescent ground'),
  projectTexture(52, 'sci_fi', 'large-concrete-panels', 'Крупные бетонные панели', 'floor', 'large concrete panels'),
  projectTexture(53, 'sci_fi', 'cyan-hex-panel', 'Шестигранная панель с голубой подсветкой', 'technical', 'cyan hex panel'),
  projectTexture(54, 'sci_fi', 'dark-cyan-circuit-floor', 'Темный пол с голубыми цепями', 'technical', 'dark cyan circuit floor'),
];

const MYSTERY_ROOM_LEGACY_TEXTURES: MysteryRoomDefaultTextureDefinition[] = [
  {
    id: 'realism_floor_brushed_concrete',
    label: 'Realism / Пол / Матовый бетон',
    style: 'realism',
    category: 'floor',
    subcategory: 'concrete',
    recommendedFor: ['floor', 'object'],
    tileSize: 96,
    assetName: 'realism_floor_brushed_concrete.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(96, [
      '<path d="M0 20H96M0 52H96M0 84H96" stroke="#334155" stroke-width="1" opacity=".45"/>',
      '<path d="M18 0V96M64 0V96" stroke="#475569" stroke-width=".8" opacity=".28"/>',
      '<path d="M0 9C23 14 45 5 69 10S91 20 96 17" fill="none" stroke="#94a3b8" stroke-width=".7" opacity=".18"/>',
      '<path d="M0 71C20 67 45 80 69 73S86 63 96 68" fill="none" stroke="#020617" stroke-width=".8" opacity=".18"/>',
      '<circle cx="15" cy="37" r="1.1" fill="#64748b" opacity=".32"/><circle cx="59" cy="29" r=".8" fill="#cbd5e1" opacity=".18"/><circle cx="83" cy="61" r="1" fill="#64748b" opacity=".22"/>',
    ].join(''), '#1f2937')),
  },
  {
    id: 'realism_floor_lab_tile',
    label: 'Realism / Пол / Лабораторная плитка',
    style: 'realism',
    category: 'floor',
    subcategory: 'lab tile',
    recommendedFor: ['floor', 'object'],
    tileSize: 128,
    assetName: 'realism_floor_lab_tile.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(128, [
      '<path d="M0 32H128M0 64H128M0 96H128M32 0V128M64 0V128M96 0V128" stroke="#dbeafe" stroke-width="2" opacity=".42"/>',
      '<path d="M0 31H128M0 63H128M0 95H128M31 0V128M63 0V128M95 0V128" stroke="#64748b" stroke-width="1" opacity=".25"/>',
      '<rect x="5" y="5" width="22" height="22" fill="#f8fafc" opacity=".06"/><rect x="69" y="37" width="22" height="22" fill="#0f172a" opacity=".07"/><rect x="101" y="101" width="22" height="22" fill="#f8fafc" opacity=".05"/>',
    ].join(''), '#94a3b8')),
  },
  {
    id: 'pixel_floor_checker_lab',
    label: 'Pixel / Пол / Контрастная лабораторная плитка',
    style: 'pixel',
    category: 'floor',
    subcategory: 'checker lab tile',
    recommendedFor: ['floor', 'object'],
    tileSize: 64,
    assetName: 'pixel_floor_checker_lab.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(64, [
      '<rect x="0" y="0" width="16" height="16" fill="#1e293b"/><rect x="16" y="0" width="16" height="16" fill="#334155"/><rect x="32" y="0" width="16" height="16" fill="#1e293b"/><rect x="48" y="0" width="16" height="16" fill="#334155"/>',
      '<rect x="0" y="16" width="16" height="16" fill="#334155"/><rect x="16" y="16" width="16" height="16" fill="#0f172a"/><rect x="32" y="16" width="16" height="16" fill="#334155"/><rect x="48" y="16" width="16" height="16" fill="#0f172a"/>',
      '<rect x="0" y="32" width="16" height="16" fill="#1e293b"/><rect x="16" y="32" width="16" height="16" fill="#334155"/><rect x="32" y="32" width="16" height="16" fill="#1e293b"/><rect x="48" y="32" width="16" height="16" fill="#334155"/>',
      '<rect x="0" y="48" width="16" height="16" fill="#334155"/><rect x="16" y="48" width="16" height="16" fill="#0f172a"/><rect x="32" y="48" width="16" height="16" fill="#334155"/><rect x="48" y="48" width="16" height="16" fill="#0f172a"/>',
      '<path d="M0 0H64V64H0Z" fill="none" stroke="#38bdf8" stroke-width="1" opacity=".22"/>',
    ].join(''), '#0f172a')),
  },
  {
    id: 'realism_wall_brushed_metal',
    label: 'Realism / Стены / Шлифованный металл',
    style: 'realism',
    category: 'wall',
    subcategory: 'metal',
    recommendedFor: ['wall', 'door', 'object'],
    tileSize: 128,
    assetName: 'realism_wall_brushed_metal.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(128, [
      '<path d="M0 16H128M0 48H128M0 80H128M0 112H128" stroke="#e2e8f0" stroke-width="1" opacity=".2"/>',
      '<path d="M0 24H128M0 25H128M0 62H128M0 99H128" stroke="#020617" stroke-width=".8" opacity=".22"/>',
      '<path d="M0 6H128M0 9H128M0 36H128M0 39H128M0 70H128M0 73H128M0 118H128" stroke="#cbd5e1" stroke-width=".6" opacity=".3"/>',
      '<circle cx="16" cy="16" r="3" fill="#0f172a" opacity=".32"/><circle cx="112" cy="16" r="3" fill="#0f172a" opacity=".32"/><circle cx="16" cy="112" r="3" fill="#0f172a" opacity=".32"/><circle cx="112" cy="112" r="3" fill="#0f172a" opacity=".32"/>',
    ].join(''), '#64748b')),
  },
  {
    id: 'horror_floor_stained_concrete',
    label: 'Horror / Пол / Загрязненный бетон',
    style: 'horror',
    category: 'floor',
    subcategory: 'stained concrete',
    recommendedFor: ['floor', 'object'],
    tileSize: 128,
    assetName: 'horror_floor_stained_concrete.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(128, [
      '<path d="M0 40H128M0 86H128" stroke="#312e81" stroke-width="1" opacity=".22"/>',
      '<path d="M18 0C22 23 14 45 18 66S31 103 26 128M91 0C80 24 97 53 91 74S75 105 83 128" fill="none" stroke="#020617" stroke-width="3" opacity=".22"/>',
      '<circle cx="41" cy="52" r="18" fill="#7f1d1d" opacity=".18"/><circle cx="48" cy="57" r="7" fill="#450a0a" opacity=".18"/><circle cx="103" cy="97" r="11" fill="#064e3b" opacity=".14"/>',
      '<path d="M0 12C35 19 63 1 96 15S119 29 128 24" fill="none" stroke="#94a3b8" stroke-width=".7" opacity=".14"/>',
    ].join(''), '#27272a')),
  },
  {
    id: 'horror_wall_cracked_tile',
    label: 'Horror / Стены / Треснувшая плитка',
    style: 'horror',
    category: 'wall',
    subcategory: 'cracked tile',
    recommendedFor: ['wall', 'door', 'object'],
    tileSize: 128,
    assetName: 'horror_wall_cracked_tile.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(128, [
      '<path d="M0 32H128M0 64H128M0 96H128M32 0V128M64 0V128M96 0V128" stroke="#9ca3af" stroke-width="1.5" opacity=".28"/>',
      '<path d="M66 0L58 23L71 34L63 56L78 71L70 99L83 128" fill="none" stroke="#020617" stroke-width="2.5" opacity=".48"/>',
      '<path d="M63 56L39 61M78 71L104 80M58 23L42 10" fill="none" stroke="#020617" stroke-width="1.5" opacity=".38"/>',
      '<rect x="5" y="69" width="22" height="22" fill="#7f1d1d" opacity=".08"/><rect x="101" y="5" width="22" height="22" fill="#020617" opacity=".12"/>',
    ].join(''), '#52525b')),
  },
  {
    id: 'pixel_floor_lab_grid',
    label: 'Pixel / Пол / Лабораторная сетка',
    style: 'pixel',
    category: 'floor',
    subcategory: 'grid',
    recommendedFor: ['floor', 'object', 'trigger'],
    tileSize: 64,
    assetName: 'pixel_floor_lab_grid.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(64, [
      '<path d="M0 16H64M0 32H64M0 48H64M16 0V64M32 0V64M48 0V64" stroke="#38bdf8" stroke-width="2" opacity=".34" shape-rendering="crispEdges"/>',
      '<path d="M0 0H64V64H0Z" fill="none" stroke="#0ea5e9" stroke-width="4" opacity=".38" shape-rendering="crispEdges"/>',
      '<rect x="18" y="18" width="12" height="12" fill="#67e8f9" opacity=".11"/><rect x="50" y="2" width="12" height="12" fill="#0f172a" opacity=".18"/><rect x="2" y="50" width="12" height="12" fill="#0f172a" opacity=".18"/>',
    ].join(''), '#111827')),
  },
  {
    id: 'pixel_wall_terminal_panel',
    label: 'Pixel / Стены / Панель терминала',
    style: 'pixel',
    category: 'wall',
    subcategory: 'terminal panel',
    recommendedFor: ['wall', 'door', 'object'],
    tileSize: 64,
    assetName: 'pixel_wall_terminal_panel.svg',
    assetMimeType: 'image/svg+xml',
    assetUrl: svgTextureDataUrl(tileSvg(64, [
      '<rect x="0" y="0" width="64" height="64" fill="#1e293b"/><path d="M0 16H64M0 48H64M16 0V64M48 0V64" stroke="#020617" stroke-width="2" opacity=".45" shape-rendering="crispEdges"/>',
      '<rect x="20" y="20" width="8" height="8" fill="#22c55e" opacity=".8"/><rect x="36" y="20" width="8" height="8" fill="#ef4444" opacity=".75"/><rect x="20" y="36" width="24" height="4" fill="#38bdf8" opacity=".7"/>',
      '<path d="M0 0H64V64H0Z" fill="none" stroke="#64748b" stroke-width="2" opacity=".55" shape-rendering="crispEdges"/>',
    ].join(''), '#1e293b')),
  },
];

export const MYSTERY_ROOM_DEFAULT_TEXTURE_STYLES: MysteryRoomDefaultTextureStyle[] = ['realism', 'horror', 'decor', 'sci_fi', 'pixel'];
export const MYSTERY_ROOM_TEXTURE_FALLBACK_TILE_SIZE = 128;

export function getMysteryRoomDefaultTexture(id: string | null | undefined): MysteryRoomDefaultTextureDefinition | null {
  if (!id) {
    return null;
  }
  return (
    MYSTERY_ROOM_DEFAULT_TEXTURES.find((texture) => texture.id === id) ??
    MYSTERY_ROOM_LEGACY_TEXTURES.find((texture) => texture.id === id) ??
    null
  );
}

export function resolveMysteryRoomTextureAssetUrl(texture: MysteryRoomSurfaceTexture | null | undefined): string | undefined {
  if (!texture) {
    return undefined;
  }
  return texture.assetUrl || getMysteryRoomDefaultTexture(texture.builtinTextureId)?.assetUrl;
}

export function getMysteryRoomTextureTileSize(
  texture: MysteryRoomSurfaceTexture | null | undefined,
  fallback = MYSTERY_ROOM_TEXTURE_FALLBACK_TILE_SIZE,
): number {
  const builtin = getMysteryRoomDefaultTexture(texture?.builtinTextureId);
  const rawSize = texture?.tileSize ?? builtin?.tileSize ?? fallback;
  if (typeof rawSize !== 'number' || !Number.isFinite(rawSize)) {
    return fallback;
  }
  return Math.max(4, Math.min(2048, Math.round(rawSize)));
}

export function normalizeMysteryRoomTextureScale(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 1;
  }
  return Math.max(0.05, Math.min(20, Math.round(value * 1000) / 1000));
}

export function getMysteryRoomTextureWorldTileSize(
  texture: MysteryRoomSurfaceTexture | null | undefined,
  sourceSize?: { width?: number; height?: number } | null,
): { width: number; height: number } {
  const fallbackTileSize = getMysteryRoomTextureTileSize(texture);
  const sourceWidth =
    typeof sourceSize?.width === 'number' && Number.isFinite(sourceSize.width) && sourceSize.width > 0
      ? sourceSize.width
      : fallbackTileSize;
  const sourceHeight =
    typeof sourceSize?.height === 'number' && Number.isFinite(sourceSize.height) && sourceSize.height > 0
      ? sourceSize.height
      : fallbackTileSize;
  const scaleX = normalizeMysteryRoomTextureScale(texture?.scaleX);
  const scaleY = normalizeMysteryRoomTextureScale(texture?.scaleY);
  const repeatX = normalizeMysteryRoomTextureScale(texture?.repeatX);
  const repeatY = normalizeMysteryRoomTextureScale(texture?.repeatY);
  return {
    width: Math.max(4, Math.round(sourceWidth / scaleX / repeatX)),
    height: Math.max(4, Math.round(sourceHeight / scaleY / repeatY)),
  };
}

export function hydrateMysteryRoomTexture(texture: MysteryRoomSurfaceTexture | undefined): MysteryRoomSurfaceTexture | undefined {
  if (!texture?.builtinTextureId) {
    return texture;
  }
  const builtin = getMysteryRoomDefaultTexture(texture.builtinTextureId);
  if (!builtin) {
    return texture;
  }
  return {
    ...texture,
    assetUrl: texture.assetUrl || builtin.assetUrl,
    assetName: texture.assetName || builtin.label,
    assetMimeType: texture.assetMimeType || builtin.assetMimeType,
    tileSize: texture.tileSize || builtin.tileSize,
  };
}
